import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import format from 'string-template';
import * as Sentry from '@sentry/browser';

import DialogRoundedModal, { ModalBody } from '../../../components/DialogRoundedModal';
import { CancelButton, SaveButton } from '../../../components/Button/ActionButtons';
import LoggedInUserContext from '../../../context/LoggedInUserContext';
import User from '../../../Model/User';
import ContentHighlight, { HighlightItemOrigin, HighlightItemType } from '../../../CoachTools/Model/ContentHighlight';
import useToast from '../../../CoachTools/hooks/useToast';
import useStorage from '../../../hooks/useStorage';
import useComponentMounted from '../../../hooks/useComponentMounted';
import { storagePaths, pathPlaceholder } from '../../../utils/firebasePaths';
import { fetchAsBlobUrl } from '../../../utils/dataProcessing';
import { toJsDate } from '../../../utils/date';
import { reportOnce } from '../../../utils/userCoachData';
import { useClientHighlightSharingEnabled } from '../../../utils/postHog';
import useContentSharingConsent, { resolveContentSharingConsent } from '../../../hooks/useContentSharingConsent';

import CropStep from './CropStep';
import PictureThumbList from './PictureThumbList';
import { resolveWeekNumber } from './utils';
import {
  FieldBlock,
  FieldLabel,
  HelperText,
  LoadingBlock,
  PictureGroup,
  StyledTextField,
} from './styles';
import texts from './texts.json';

/** Where crops live. The client's own image folder is the only path an AC may write to. */
const HIGHLIGHT_STORAGE_SUBFOLDER = 'highlights';

/*
  No forced crop ratio. A pair's halves are stacked in a 9:16 story (each roughly 9:8) and side by side
  in a 1:1 post (each 1:2), so no single ratio can match both frames — the card covers whatever it is
  given. Locking one would misrepresent the other format while making the AC fight the frame on
  portrait progress photos.
*/
const FREE_ASPECT_RATIO = null;

const CONSENT_SOURCE = 'POST_PAYMENT_FORM';

/*
  Where the check ran, not proof that it passed. The batch is written from the browser with nothing
  server-side re-checking, so this records that this code path resolved consent before writing — any
  future write that skips the guard would carry an identical `granted: true`. Written only on the photo
  path, because that is the only one that resolves anything: a quote or a check-in answer carries no
  `consent` field at all rather than one asserting a check that never ran. Enforcement needs consent
  resolved onto the user record where rules can read it (follow-up on S2-8477).
*/
const CONSENT_CHECKED_BY = 'WEB_UI';

/** Which half of the comparison a photo is. Ordered as the AC crops them, not as the card reads. */
const PhotoRole = {
  SELECTED: 'SELECTED',
  BASELINE: 'BASELINE',
};

/**
 * Assembles a highlight task from a client's content.
 *
 * Opens from two places: the action on a client chat message, which seeds a quote, and the action on a
 * check-in photo, which seeds a before/after pair. Photos are cropped here before they are uploaded —
 * that crop is the only thing protecting the client's face, since nothing detects or blurs one.
 *
 * There is deliberately no card preview: the app is the only thing that renders a card, so a preview
 * here would be a second implementation to keep in visual sync with the first. The coach sees and can
 * edit the composed cards on their phone.
 *
 * This writes the highlight and its coach task directly, in one batch. There is no endpoint behind it
 * any more, so the checks below are the only ones there are: what a highlight claims about a message or
 * a check-in is now asserted by this component rather than re-verified against Firestore. Both entry
 * points are gated on the `admin` claim, which already permits writing the coach task and the client's
 * image folder.
 */
const ShareAsHighlightModal = ({
  isOpen,
  onClose,
  userId,
  viewAsCoachId,
  clientName,
  quoteSource,
  answerSource,
  photoPair,
}) => {
  const { showToast } = useToast();
  const { userId: loggedInUserId, userDoc: loggedInUserDoc } = useContext(LoggedInUserContext);
  const { uploadAttachments, deleteAttachments } = useStorage();
  const isComponentMountedRef = useComponentMounted();

  /*
    Seeded once, on mount. Both call sites render this modal only while it is open, so mounting *is*
    opening, and reopening starts clean because it remounts.

    This used to be an effect keyed on the props, which was a bug: `MessagePreview` builds
    `quoteSource` as an inline object, so its identity changed on every parent render — and it is a
    MobX observer, so reactions and delivery status re-render it. The effect refired and reset the
    quote mid-sentence while discarding crops the AC had already confirmed one by one.
  */
  const [taskTitle, setTaskTitle] = useState(() => format(texts.taskTitleSeed, { name: clientName }).trim());
  const [quote, setQuote] = useState(() => quoteSource?.text || answerSource?.text || '');
  const [clientMessage, setClientMessage] = useState('');
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [croppedPhotos, setCroppedPhotos] = useState([]);
  const [clientDoc, setClientDoc] = useState(null);
  const [isPreparing, setIsPreparing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Every croppable URL we create, so none is leaked when the modal closes.
  const objectUrlsRef = useRef([]);

  const trackObjectUrl = useCallback((url) => {
    objectUrlsRef.current.push(url);
    return url;
  }, []);

  const releaseObjectUrls = useCallback(() => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current = [];
  }, []);

  useEffect(() => releaseObjectUrls, [releaseObjectUrls]);

  const isSharingEnabledForCoach = useClientHighlightSharingEnabled(viewAsCoachId);
  const {
    requiresFaceBlur,
    requiresClientApproval,
    isReady: isConsentReady,
  } = useContentSharingConsent(photoPair ? userId : null);
  /*
    The client answered "Ask me each time before use": the photos go to them for a yes before the coach sees
    anything. Only labels the modal; the write itself goes by the answer re-resolved on submit.
  */
  const isAskingClient = !!photoPair && requiresClientApproval;

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  /*
    One open-time load, because the pair's crop steps need the client's record before they can be
    labelled. `serviceStartAt` turns each photo's date into a program week, which is shown above the
    cropper and stored on the item — so the card never has to reach for the client's record to render
    a "Week 11" badge.

    A check-in pair also arrives as two remote URLs, fetched into blobs before the cropper sees them:
    a cross-origin image taints the canvas and `toDataURL()` then throws.
  */
  useEffect(() => {
    let isActive = true;
    (async () => {
      let client;

      try {
        client = await User.getById(userId, { viewAsCoachId });
      } catch (error) {
        Sentry.captureException(error, {
          extra: { message: 'Failed to load the client for a content highlight', userId },
        });
        if (isActive) {
          showToast(texts.clientLoadFailed, { error: true });
          onCloseRef.current();
        }
        return;
      }

      if (!isActive) {
        return;
      }
      setClientDoc(client);

      try {
        if (!photoPair) {
          return;
        }

        /*
          The photo the AC actually clicked comes first, then the earlier one it will be compared
          against. Queueing the baseline first meant clicking a photo on the "Now" slider opened a
          months-old picture, which reads as the wrong photo rather than as step one of two.

          `before` is absent when the selected check-in is the client's first, or when they had not
          uploaded this pose yet. One photo then makes one card, rather than a pair with a hole in it.
        */
        const halves = [
          { role: PhotoRole.SELECTED, url: photoPair.after.url, at: photoPair.after.at },
          ...(photoPair.before?.url
            ? [{ role: PhotoRole.BASELINE, url: photoPair.before.url, at: photoPair.before.at }]
            : []),
        ];
        const prepared = await Promise.all(halves.map(async (half) => {
          const takenAt = half.at ? new Date(half.at) : undefined;
          const contractEntry = client?.contractEntryAtDate(takenAt);

          /*
            A dated photo no episode covers cannot be dated at all — the gap that silently costs
            the badge. Reported on the episode rather than on the week, because a missing week is
            also the by-design answer for a photo taken before its episode started, and alerting
            on that would bury this. `reportOnce` keeps a reopened modal from repeating it.
          */
          if (takenAt && !contractEntry) {
            reportOnce(
              `highlightWeek:${userId}:${viewAsCoachId}`,
              'No contract episode covers a highlight photo date',
              {
                userId,
                viewAsCoachId,
                resolvedCoachId: client?.resolvedCoachId,
                contractIds: Object.keys(client?.data?.contracts || {}),
                photoDate: half.at,
              },
            );
          }

          return {
            id: uuidv4(),
            url: await fetchAsBlobUrl(half.url),
            role: half.role,
            weekNumber: resolveWeekNumber(toJsDate(contractEntry?.serviceStartAt), takenAt),
          };
        }));

        if (!isActive) {
          prepared.forEach(({ url }) => URL.revokeObjectURL(url));
          return;
        }
        prepared.forEach(({ url }) => trackObjectUrl(url));
        setPendingPhotos(prepared);
      } catch (error) {
        Sentry.captureException(error, { extra: { message: 'Failed to prepare a content highlight', userId } });
        showToast(texts.photoLoadFailed, { error: true });
      } finally {
        if (isActive) {
          setIsPreparing(false);
        }
      }
    })();
    return () => { isActive = false; };
  }, [photoPair, userId, viewAsCoachId, showToast, trackObjectUrl]);

  const activePhoto = pendingPhotos[0];

  const handleCropConfirmed = useCallback((dataUrl) => {
    setCroppedPhotos((current) => [...current, { ...activePhoto, dataUrl }]);
    setPendingPhotos((current) => current.slice(1));
  }, [activePhoto]);

  /*
    Removing one crop keeps the other: a comparison needs two photos, but a single one is still a
    perfectly good card, so dropping the earlier photo means "post this one on its own". This is the
    only way to drop a photo — the crop step offers Cancel, which leaves the modal, because mid-queue
    there is nothing yet to judge a single photo against.
  */
  const handleRemoveCropped = useCallback((id) => {
    setCroppedPhotos((current) => current.filter((photo) => photo.id !== id));
  }, []);

  /**
   * Uploads every crop and returns the document items, plus the storage paths written — the caller
   * needs those to clean up if the write itself then fails.
   *
   * Crops go straight to Storage: the AC's `admin` claim already permits writing to the client's image
   * folder, and only the crop is ever uploaded, so there is no un-cropped copy for a later card edit to
   * reveal.
   */
  const uploadCropsAndBuildItems = useCallback(async () => {
    const items = [];
    const createdAt = new Date();
    const itemBase = () => ({ id: uuidv4(), createdAt, createdBy: loggedInUserId });

    /*
      A quote names the message it came from, and keeps the client's original wording in `source.text`
      even after the AC trims it — so a card whose words were later edited can still be checked against
      what was actually said. The chat action only appears on a client's own message, which is why the
      sender is the client.
    */
    if (quoteSource && quote.trim()) {
      items.push({
        ...itemBase(),
        type: HighlightItemType.CHAT_QUOTE,
        source: {
          origin: HighlightItemOrigin.CHAT,
          channelId: quoteSource.channelId,
          messageId: quoteSource.messageId,
          text: quoteSource.text || '',
          senderUserId: userId,
        },
        design: { widgetConfigs: { chatBubble: { text: quote.trim() } } },
      });
    }

    /*
      Same card as a quote, and the same trimming — the difference is what makes it checkable: a check-in
      and a question rather than a channel and a message. The question wording travels with it because a
      coach can change a question after the fact, leaving a key that no longer means what it did.
    */
    if (answerSource && quote.trim()) {
      items.push({
        ...itemBase(),
        type: HighlightItemType.CHECK_IN_ANSWER,
        source: {
          origin: HighlightItemOrigin.CHECK_IN,
          checkInId: answerSource.checkInId,
          questionKey: answerSource.questionKey,
          question: answerSource.question,
          text: answerSource.text || '',
        },
        design: { widgetConfigs: { chatBubble: { text: quote.trim() } } },
      });
    }

    if (!croppedPhotos.length) {
      return { items, assetPaths: [] };
    }

    const storagePath = `${format(storagePaths.USER_IMAGES, {
      [pathPlaceholder.USER_ID]: userId,
    })}/${HIGHLIGHT_STORAGE_SUBFOLDER}`;

    const uploads = await uploadAttachments(
      croppedPhotos.map((photo) => ({
        dataUrl: photo.dataUrl,
        format: 'jpeg',
        fileName: `highlight-${photo.role.toLowerCase()}`,
        fileType: 'image/jpeg',
        fileId: photo.id,
      })),
      storagePath,
    );

    const pathById = new Map();
    const assetPaths = [];
    uploads.forEach((upload) => {
      if (upload.fileRef) {
        pathById.set(upload.fileId, upload.fileRef);
        assetPaths.push(upload.fileRef);
      }
    });

    if (pathById.size !== croppedPhotos.length) {
      // Some landed and some did not. Carry the paths out so the caller can still delete those.
      const failure = new Error('Not every cropped photo could be uploaded');
      failure.assetPaths = assetPaths;
      throw failure;
    }

    const halves = {};
    croppedPhotos.forEach((photo) => {
      halves[photo.role] = {
        assetPath: pathById.get(photo.id),
        ...(photo.weekNumber === undefined ? {} : { weekNumber: photo.weekNumber }),
      };
    });

    const before = halves[PhotoRole.BASELINE];
    const after = halves[PhotoRole.SELECTED];
    const source = {
      origin: HighlightItemOrigin.CHECK_IN,
      checkInId: photoPair.checkInId,
      attachmentIndex: photoPair.attachmentIndex,
    };

    if (before && after) {
      items.push({
        ...itemBase(), type: HighlightItemType.PHOTO_COMPARE, source, before, after,
      });
    } else if (before || after) {
      /*
        One half left, so one photo and one card. Reached two ways: the client's first check-in has no
        earlier photo to pair with, and the AC can drop either half themselves. Both keep the check-in
        reference, because the remaining photo still came from that check-in.
      */
      items.push({
        ...itemBase(), type: HighlightItemType.PHOTO, source, photo: after || before,
      });
    }

    return { items, assetPaths };
  }, [quote, quoteSource, answerSource, croppedPhotos, photoPair, uploadAttachments, userId, loggedInUserId]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    /*
      A coach who is their own client has no one to deliver to. Real: coaches are self-assigned as
      clients so they can see the app as one, and the check-in entry point is reachable on that profile.
    */
    if (!viewAsCoachId || viewAsCoachId === userId) {
      showToast(texts.noCoach, { error: true });
      return;
    }

    // A toast rather than a disabled Create, which would give the AC no reason for it.
    if (!isSharingEnabledForCoach) {
      showToast(texts.coachNotEnabled, { error: true });
      return;
    }

    /*
      Only when a photo of the client is actually about to be published. The question asks about their
      progress photos, so a quote or a check-in answer — their own words — is not held back by it.

      Resolves the per-client cache the render-time hook already filled, so this re-checks that value
      rather than issuing a fresh read — it cannot observe a change made after the first render in this
      session. Adequate only while consent is write-once: clients have no way to withdraw it yet. When
      withdrawal ships this has to bypass the cache, and `setIsSubmitting` has to move above the await,
      since a real query leaves Create enabled long enough to double-submit.
    */
    /*
      A client who asked to be asked is not refused: the highlight is written as a draft instead, which the
      app puts to them, and the coach only gets the task once they agree. A plain yes still wins over that.
    */
    let needsClientApproval = false;
    if (croppedPhotos.length) {
      const { isAllowed, requiresClientApproval: mustAskClient } = await resolveContentSharingConsent(userId);
      if (!isAllowed && !mustAskClient) {
        showToast(texts.noConsent, { error: true });
        return;
      }
      needsClientApproval = !isAllowed;
    }

    setIsSubmitting(true);
    // Crops are uploaded before the documents, so a failed write leaves files behind unless we clear up.
    let uploadedPaths = [];
    try {
      const built = await uploadCropsAndBuildItems().catch((error) => {
        // A partial upload still wrote files; carry their paths out so `finally` can remove them.
        uploadedPaths = error.assetPaths || [];
        throw error;
      });
      uploadedPaths = built.assetPaths;

      if (!built.items.length) {
        showToast(texts.nothingToSend, { error: true });
        return;
      }

      await ContentHighlight.createWithTask({
        userId,
        coachId: viewAsCoachId,
        items: built.items,
        createdBy: loggedInUserId,
        createdByName: loggedInUserDoc?.name,
        title: taskTitle.trim(),
        userName: clientDoc?.name,
        userAvatar: clientDoc?.avatarUrl,
        // A draft asserts nothing: the client's app records their own yes when they give it.
        consent: croppedPhotos.length && !needsClientApproval
          ? {
            granted: true, source: CONSENT_SOURCE, checkedBy: CONSENT_CHECKED_BY, checkedAt: new Date(),
          }
          : undefined,
        requiresClientApproval: needsClientApproval,
        clientMessage: needsClientApproval ? clientMessage.trim() : undefined,
      });

      uploadedPaths = [];
      showToast(needsClientApproval ? texts.sentForApproval : texts.created);
      onClose();
    } catch (error) {
      Sentry.captureException(error, { extra: { message: 'Failed to create content highlight', userId } });
      showToast(texts.failed, { error: true });
    } finally {
      /*
        Nothing references these crops — the highlight that would have was never written — and they sit
        in the client's own image folder, where an orphan is not just clutter but a stray photo of them.
        Best-effort: a failed cleanup must not replace the error the AC actually needs to see.
      */
      if (uploadedPaths.length) {
        deleteAttachments(uploadedPaths.map((storagePath) => ({ storagePath }))).catch((error) => {
          Sentry.captureException(error, { extra: { message: 'Failed to clean up orphaned highlight crops', userId } });
        });
      }
      if (isComponentMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }, [
    isSubmitting,
    clientDoc,
    viewAsCoachId,
    isSharingEnabledForCoach,
    croppedPhotos,
    uploadCropsAndBuildItems,
    userId,
    loggedInUserId,
    loggedInUserDoc,
    taskTitle,
    clientMessage,
    showToast,
    onClose,
    deleteAttachments,
    isComponentMountedRef,
  ]);

  /*
    Chronological, rather than in crop order: the AC crops their selection first, but the card reads
    earlier-then-later, and the thumbnails stand for the card.
  */
  const comparePhotos = [];
  croppedPhotos.forEach((photo) => {
    if (photo.role === PhotoRole.BASELINE) {
      comparePhotos.unshift(photo);
    } else {
      comparePhotos.push(photo);
    }
  });

  /*
    Holds the crop step until consent has resolved, rather than letting it render a beat without the
    face-blur callout. Costs nothing on the quote path, which is passed a null userId and so is ready
    on mount, and nothing on the photo path either, where the photos are still being fetched.
  */
  const isLoading = isPreparing || !isConsentReady;

  const canSubmit = !!taskTitle.trim()
    && (!!quote.trim() || croppedPhotos.length > 0)
    && !pendingPhotos.length
    && !!clientDoc;

  const submitLabels = isAskingClient
    ? { idle: texts.sendForApproval, busy: texts.sendingForApproval }
    : { idle: texts.create, busy: texts.creating };
  const submitLabel = isSubmitting ? submitLabels.busy : submitLabels.idle;

  /** Names which photo is in frame. The week is what tells the AC they picked the right check-in. */
  const cropLabelFor = (photo) => {
    if (photo.role === PhotoRole.BASELINE) {
      return texts.cropBaselineLabel;
    }
    return photo.weekNumber === undefined
      ? texts.cropSelectedLabel
      : format(texts.cropWeekLabel, { week: photo.weekNumber });
  };

  return (
    <DialogRoundedModal
      open={isOpen}
      onClose={onClose}
      title={texts.title}
      description={isAskingClient ? texts.descriptionAskClient : texts.description}
      // The crop step and the loading state carry their own actions, or none.
      actionButtons={activePhoto || isLoading ? null : (
        <>
          <CancelButton onClick={onClose}>{texts.cancel}</CancelButton>
          <SaveButton onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
            {submitLabel}
          </SaveButton>
        </>
      )}
    >
      <ModalBody>
        {isLoading && (
          <LoadingBlock>
            <CircularProgress size={28} />
            <HelperText>{photoPair ? texts.loadingPhotos : texts.loading}</HelperText>
          </LoadingBlock>
        )}
        {!isLoading && activePhoto && (
          <CropStep
            imageUrl={activePhoto.url}
            label={cropLabelFor(activePhoto)}
            aspectRatio={FREE_ASPECT_RATIO}
            requiresFaceBlur={requiresFaceBlur}
            onConfirm={handleCropConfirmed}
            onCancel={onClose}
          />
        )}
        {!isLoading && !activePhoto && (
          <>
            <FieldBlock>
              <FieldLabel>{texts.taskTitleLabel}</FieldLabel>
              <StyledTextField
                value={taskTitle}
                onChange={(event) => setTaskTitle(event.target.value)}
                placeholder={texts.taskTitlePlaceholder}
              />
            </FieldBlock>

            {/*
              Only when the client's own words seeded it — a message or a check-in answer. A highlight
              built from a check-in photo carries no words, so there is nothing there for the AC to trim,
              and a free-text box on that path would produce a card whose words nothing can attribute to
              the client.
            */}
            {(!!quoteSource || !!answerSource) && (
              <FieldBlock>
                <FieldLabel>{answerSource ? texts.answerLabel : texts.quoteLabel}</FieldLabel>
                <StyledTextField
                  multiline
                  minRows={3}
                  maxRows={8}
                  value={quote}
                  onChange={(event) => setQuote(event.target.value)}
                  placeholder={texts.quotePlaceholder}
                />
                <HelperText>{texts.helper}</HelperText>
              </FieldBlock>
            )}

            {comparePhotos.length > 0 && (
              <FieldBlock>
                <FieldLabel>{texts.comparePairLabel}</FieldLabel>
                <PictureGroup>
                  <PictureThumbList pictures={comparePhotos} onRemove={handleRemoveCropped} />
                  <HelperText>{texts.comparePairHelper}</HelperText>
                </PictureGroup>
              </FieldBlock>
            )}

            {/* What the client reads under the photos when the app asks them. Empty shows the photos alone. */}
            {isAskingClient && (
              <FieldBlock>
                <FieldLabel>{texts.clientMessageLabel}</FieldLabel>
                <StyledTextField
                  multiline
                  minRows={2}
                  maxRows={5}
                  value={clientMessage}
                  onChange={(event) => setClientMessage(event.target.value)}
                  placeholder={texts.clientMessagePlaceholder}
                />
                <HelperText>{texts.clientMessageHelper}</HelperText>
              </FieldBlock>
            )}
          </>
        )}
      </ModalBody>
    </DialogRoundedModal>
  );
};

ShareAsHighlightModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  /** Coach to read the client's contracts through. Optional: chat has no coach, and a quote has no week. */
  viewAsCoachId: PropTypes.string,
  clientName: PropTypes.string,
  quoteSource: PropTypes.shape({
    channelId: PropTypes.string.isRequired,
    messageId: PropTypes.string.isRequired,
    text: PropTypes.string,
  }),
  /** A written check-in answer. Produces the same card as a quote, from a different claim. */
  answerSource: PropTypes.shape({
    checkInId: PropTypes.string.isRequired,
    questionKey: PropTypes.string.isRequired,
    question: PropTypes.string,
    text: PropTypes.string,
  }),
  photoPair: PropTypes.shape({
    checkInId: PropTypes.string.isRequired,
    attachmentIndex: PropTypes.number.isRequired,
    before: PropTypes.shape({ url: PropTypes.string, at: PropTypes.string }),
    after: PropTypes.shape({ url: PropTypes.string, at: PropTypes.string }).isRequired,
  }),
};

ShareAsHighlightModal.defaultProps = {
  viewAsCoachId: undefined,
  clientName: '',
  quoteSource: null,
  answerSource: null,
  photoPair: null,
};

export default ShareAsHighlightModal;
